/**
 * "Risk Metrics" tab of the MARS portfolio dashboard.
 *
 * Renders a 2-up grid of risk charts. The metric set switches with the
 * Absolute / Relative view toggle (`ABSOLUTE_METRICS` vs
 * `RELATIVE_METRICS`); each metric is its own query, all run together via
 * `useQueries`. "Unified" because it replaced separate absolute-only and
 * relative-only components.
 *
 * Per-fund variation: funds with `risk_metrics_type === 'absolute'` hide
 * the view toggle, and only DAR-NN / Fidelity / FQM funds send scenario
 * toggle params (see `buildToggles`).
 */
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import PlotlyChart from '@/lib/plotly/PlotlyChart';
import { ChartCard } from '@/components/ChartCard';
import QueryState from '@/components/QueryState';
import SegmentedControl from '@/components/SegmentedControl';

import { fetchRiskMetrics, marsPortfolioKeys, type ToggleParams } from '../api';
import { buildRiskMetricPlot, type MetricMeta } from '../lib/riskMetricsChart';
import { SectionHeader } from './sectionUi';
import { useMarsPortfolioStore, type RiskView } from '../store';
import type { Fund, RiskMetricRow, RiskMetricsResponse, RiskPeriod } from '../types';

interface RiskMetricsUnifiedProps {
  fund: Fund;
}

const ABSOLUTE_METRICS: MetricMeta[] = [
  {
    id: 'ex_ante_abs_vol',
    title: 'Ex-ante Volatility (Absolute)',
    singleSeries: true,
    isContribution: false,
  },
  { id: 'cvar_absolute', title: 'Absolute CVaR(5%)', singleSeries: false, isContribution: false },
  {
    id: 'absolute_risk_contribution',
    title: 'Absolute Risk Contribution',
    singleSeries: false,
    isContribution: true,
  },
  {
    id: 'prob_5pct_loss_absolute',
    title: 'Probability of 5% Loss',
    singleSeries: false,
    isContribution: false,
  },
];

const RELATIVE_METRICS: MetricMeta[] = [
  {
    id: 'ex_ante_rel_vol',
    title: 'Ex-ante Tracking Error Volatility',
    singleSeries: true,
    isContribution: false,
  },
  { id: 'cvar_relative', title: 'Relative CVaR(5%)', singleSeries: true, isContribution: false },
  {
    id: 'relative_risk_contribution',
    title: 'Relative Risk Contribution',
    singleSeries: false,
    isContribution: true,
  },
  {
    id: 'prob_2pct_loss_relative',
    title: 'Probability of 2% Loss (Relative)',
    singleSeries: true,
    isContribution: false,
  },
];

const PERIOD_OPTIONS: ReadonlyArray<{ value: RiskPeriod; label: string }> = [
  { value: '6m', label: '6M' },
  { value: '1y', label: '1Y' },
  { value: '3y', label: '3Y' },
  { value: '10y', label: '10Y' },
  { value: '20y', label: '20Y' },
];

/** Short fund name shown inside the description prose. */
function fundShortName(fundId: string): string {
  switch (fundId) {
    case 'mars-qmas':
      return 'QMAS';
    case 'mars-qmas-nn':
      return 'QMAS NN';
    case 'mars-dar-nn':
      return 'DAR NN';
    case 'mars-dar':
      return 'DAR';
    case 'mat-ucits':
      return 'MAT UCITS';
    case 'mars-fqm':
      return 'FQM';
    case 'mars-fidelity-balanced':
      return 'Balanced Portfolio';
    case 'mars-fidelity-conservative':
      return 'Conservative Portfolio';
    default:
      return 'selected portfolio';
  }
}

/** View-button labels vary by `risk_metrics_type`. */
function viewOptionsFor(
  riskMetricsType: string,
): ReadonlyArray<{ value: RiskView; label: string }> {
  if (riskMetricsType === 'absolute_relative') {
    return [
      { value: 'absolute', label: 'Absolute Risk Metrics' },
      { value: 'contribution', label: 'Relative Risk Metrics' },
    ];
  }
  return [
    { value: 'absolute', label: 'Portfolio Risk Metrics' },
    { value: 'contribution', label: 'Relative Risk Contribution' },
  ];
}

/** Toggle query params — only DAR-NN, Fidelity and FQM funds send any. */
function buildToggles(
  fundId: string,
  volTiming: boolean,
  constraints: boolean,
  tc: boolean,
): ToggleParams {
  if (fundId === 'mars-dar-nn') return { vol_timing: volTiming, constraints, tc };
  if (fundId.startsWith('mars-fidelity-') || fundId === 'mars-fqm') {
    return { vol_timing: volTiming, tc };
  }
  return {};
}

export default function RiskMetricsUnified({ fund }: RiskMetricsUnifiedProps): JSX.Element {
  const riskPeriod = useMarsPortfolioStore((s) => s.riskPeriod);
  const setRiskPeriod = useMarsPortfolioStore((s) => s.setRiskPeriod);
  const riskView = useMarsPortfolioStore((s) => s.riskView);
  const setRiskView = useMarsPortfolioStore((s) => s.setRiskView);
  const volTiming = useMarsPortfolioStore((s) => s.volTiming);
  const constraints = useMarsPortfolioStore((s) => s.constraints);
  const tc = useMarsPortfolioStore((s) => s.tc);

  const metrics = riskView === 'absolute' ? ABSOLUTE_METRICS : RELATIVE_METRICS;

  const toggles = useMemo(
    () => buildToggles(fund.id, volTiming, constraints, tc),
    [fund.id, volTiming, constraints, tc],
  );

  const queries = useQueries({
    queries: metrics.map((m) => ({
      queryKey: marsPortfolioKeys.riskMetrics(fund.id, m.id, riskPeriod, toggles),
      queryFn: (): Promise<RiskMetricsResponse> =>
        fetchRiskMetrics(fund.id, m.id, riskPeriod, toggles),
      staleTime: 60_000,
    })),
  });

  const supportsRelative = fund.risk_metrics_type !== 'absolute';
  const viewOptions = viewOptionsFor(fund.risk_metrics_type);
  const shortName = fundShortName(fund.id);

  return (
    <section>
      <div className="px-4">
        <SectionHeader
          title="Risk Metrics"
          description={
            <>
              This page displays the internal risk metrics for MARS within the {shortName}. Based on
              the portfolio weights shown under Asset Positions, the agent computes a wide range of
              absolute and relative risk measures. These metrics provide a deeper understanding of
              the key risks the portfolio is taking and how they evolve over time.
            </>
          }
        />

        <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
          <SegmentedControl
            variant="group-secondary"
            label="Period:"
            options={PERIOD_OPTIONS}
            value={riskPeriod}
            onChange={setRiskPeriod}
          />
          {supportsRelative ? (
            <SegmentedControl
              variant="group-secondary"
              label="View:"
              options={viewOptions}
              value={riskView}
              onChange={setRiskView}
            />
          ) : null}
        </div>
      </div>

      <div className="px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {metrics.map((meta, idx) => {
            const q = queries[idx];
            return (
              <RiskMetricPlot
                key={meta.id}
                meta={meta}
                isLoading={q.isLoading}
                isError={q.isError || q.data?.success === false}
                data={q.data?.data}
                fund={fund}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}

interface RiskMetricPlotProps {
  meta: MetricMeta;
  fund: Fund;
  isLoading: boolean;
  isError: boolean;
  data: RiskMetricRow[] | undefined;
}

function RiskMetricPlot({
  meta,
  fund,
  isLoading,
  isError,
  data,
}: RiskMetricPlotProps): JSX.Element {
  const plot = useMemo(() => buildRiskMetricPlot(meta, fund, data), [meta, fund, data]);

  return (
    <ChartCard>
      <QueryState
        query={{ isLoading, isError, data: plot }}
        isEmpty={(p) => p.data.length === 0}
        loadingLabel="Loading…"
        errorLabel={`Failed to load ${meta.title}`}
        emptyLabel="No data available"
      >
        {(p) => <PlotlyChart data={p.data} layout={p.layout} />}
      </QueryState>
    </ChartCard>
  );
}
